import { useState, useEffect } from "react";
import { API_URLS } from "./config";
import axios from "axios";
import { useLanguage } from "LanguageContext";

interface UserData {
  token: string | null;
  name: string;
  position: string;
  email: string;
  picture: string | null;
  showAnalysisResultConsent: boolean | null;
  localeCode: "en" | "fr";
  isAdmin: boolean;
  is_llid_user: boolean;
}
declare global {
  interface Window {
    referencesFramework?: any; // or a more specific type if you know it
  }
}
export function useUserDataService() {
  const [userData, setUserData] = useState<UserData>({
    name: "Visitor",
    position: "Visitor",
    picture: null,
    token: null,
    email: "visitor@publicissapient.com",
    showAnalysisResultConsent: null,
    localeCode: "en",
    isAdmin: false,
    is_llid_user: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lang = useLanguage();
  const domain = window.location.hostname;
  const whitelisted_path = ['/changepassword', '/signin', '/password', '/success'];

  useEffect(() => {
    const fetchTokenAndUserData = async () => {
      try {
        const tokenResponse = await axios
          .get(`${API_URLS.TOKEN_INFO}`, {
            params: {
              credentials: "include",
            },
          })
          .catch((error) => {
            if (error.response && error.response.data) {
              localStorage.removeItem("userToken");
              if (!whitelisted_path?.includes(window.location.pathname)) {
                console.log(error, '--error');
                window.open(error.response.data.login_url.auth_uri, "_self");
              } else{
                setUserData((prevData) => ({
                  ...prevData, token: ' '}));
                setLoading(false);
              }
            }
          });

        // store the current user or reset
        if (tokenResponse) {
          if (tokenResponse.data.token) {
            localStorage.setItem("userToken", tokenResponse.data.token);
          }

          // continue
          const userInfoResponse = await axios.get(`${API_URLS.USER_INFO}`, {
            params: {
              credentials: "include",
            },
          });

          if (userInfoResponse.status != 200) {
            throw new Error("Failed to retrieve user data.");
          }

          let showAnalysisResultConsent = null;
          if (userInfoResponse.data?.consent_date) {
            const lastConsentDate = new Date((new Date(userInfoResponse.data?.consent_date)).getTime() + 30 * 24 * 60 * 60 * 1000);
            showAnalysisResultConsent = lastConsentDate < (new Date());
          }

          setUserData({
            name: `${userInfoResponse.data.first_name} ${userInfoResponse.data.last_name}`,
            position: userInfoResponse.data.position,
            picture: userInfoResponse.data.picture,
            email: userInfoResponse.data.business_email,
            token: tokenResponse.data.token,
            showAnalysisResultConsent,
            localeCode: userInfoResponse.data.locale_code,
            isAdmin: userInfoResponse.data.is_admin,
            is_llid_user: userInfoResponse.data.is_llid_user,
          });
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
      
    fetchTokenAndUserData();
  }, []);

  useEffect(()=>{
     const fetchReferencesData = async () => {
          const response = await axios.get(API_URLS?.REFERENCES_FRAMEWORKS, {
            params: {
              locale: 'en',
              populate: '*',
                filters:{
                  frontend_types: {
                    hostname: {
                        $eq: domain,
                      },
                    },
                  },
            }
          });
         window.referencesFramework = {};
         window.referencesFramework = {};
         response?.data?.data.forEach((item: any) => {
           let _subDomain_key = item?.Criteriaid+'.title';
           let primary_lan = item?.locale;
           let seconday_lan = item?.localizations?.[0]?.locale || '';
           let titlekeySet =  item?.Criteriaid?.trim() || "";
           if(!window?.referencesFramework?.[titlekeySet]){
             window.referencesFramework[titlekeySet] = {
               [primary_lan]: {
                 "title": item?.Domains,
                 "subDomains": {},
               },
               [seconday_lan]: {
                 "title": item?.localizations?.[0]?.Domains,
                 "subDomains": {},
               }
             };
           }
           if(!window?.referencesFramework?.[titlekeySet]?.[primary_lan]?.subDomains?.[_subDomain_key]){
             window.referencesFramework[titlekeySet][primary_lan].subDomains[_subDomain_key] = {
               'title': item?.Subdomains,
               'criteria': []
             };
           }
           if(!window?.referencesFramework?.[titlekeySet]?.[seconday_lan]?.subDomains?.[_subDomain_key]){
             window.referencesFramework[titlekeySet][seconday_lan].subDomains[_subDomain_key] = {
               'title': item?.localizations?.[0]?.Subdomains,
               'criteria': []
             };
           }
           
           window.referencesFramework[titlekeySet][primary_lan].subDomains[_subDomain_key].criteria.push({
             'title': item?.Criteria,
             'details': item?.Details,
           });
         
           window.referencesFramework[titlekeySet][seconday_lan].subDomains[_subDomain_key].criteria.push({
               'title': item?.localizations?.[0]?.Criteria,
               'details': item?.localizations?.[0]?.Details,
           });
         });
      }   
   fetchReferencesData();
  },[])

  return { userData, loading, error };
}
